// Campaign ETA calculator.
//
// Given a campaign snapshot (total recipients, processed count, batch size,
// pause range, worker count, and optional started_at), returns the estimated
// number of seconds remaining plus a human-readable string.
//
// Two modes:
//   - "measured": we have started_at + at least 10 processed rows → use the
//     actual wall-clock rate, backing out the pauses that have already elapsed.
//   - "estimated": fall back to a per-email default (5 sec / worker) so we can
//     show a plan-time estimate before the campaign runs.
//
// Consumers just pass the campaign object; the function handles missing fields.

const DEFAULT_PER_EMAIL_SEC = 5; // conservative average for SMTP over proxy

export function humanDuration(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return rs > 0 ? `${m}m ${rs}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  if (h < 24) return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
}

/**
 * Compute a plan-time estimate for a campaign that hasn't started yet.
 * inputs is expected to contain: recipient_count, batch_size, pause_minimum_seconds,
 *   pause_maximum_seconds, worker_count.
 */
export function estimateCampaignPlanTime(inputs) {
  const total = Number(inputs.recipient_count) || 0;
  const batchSize = Math.max(1, Number(inputs.batch_size) || 250);
  const workers = Math.max(1, Number(inputs.worker_count) || 10);
  const pauseMin = Number(inputs.pause_minimum_seconds) || 0;
  const pauseMax = Number(inputs.pause_maximum_seconds) || pauseMin;
  const avgPause = (pauseMin + pauseMax) / 2;
  if (total <= 0) return { seconds: 0, human: "0s", method: "estimated", perEmail: 0 };
  const wallClockPerEmail = DEFAULT_PER_EMAIL_SEC / workers;
  const batches = Math.ceil(total / batchSize);
  const totalSend = total * wallClockPerEmail;
  const totalPause = Math.max(0, batches - 1) * avgPause;
  const seconds = Math.round(totalSend + totalPause);
  return {
    seconds,
    human: humanDuration(seconds),
    method: "estimated",
    perEmail: wallClockPerEmail,
    remainingBatches: batches,
  };
}

/**
 * Compute the remaining time for a running / paused / draft campaign snapshot.
 * `c` is expected to include: total (or total_processed + remaining),
 *   total_processed, batch_size, pause_minimum_seconds, pause_maximum_seconds,
 *   worker_count, started_at.
 * `now` is an override for tests.
 */
export function estimateCampaignEta(c, now = Date.now()) {
  const total =
    Number(c.total) ||
    (Number(c.total_processed || 0) + Number(c.remaining || 0));
  const done = Number(c.total_processed) || 0;
  const remaining = Math.max(0, total - done);
  if (remaining === 0) {
    return { seconds: 0, human: "—", method: "done", perEmail: 0, percent: 100 };
  }

  const batchSize = Math.max(1, Number(c.batch_size) || 250);
  const workers = Math.max(1, Number(c.worker_count) || 10);
  const pauseMin = Number(c.pause_minimum_seconds) || 0;
  const pauseMax = Number(c.pause_maximum_seconds) || pauseMin;
  const avgPause = (pauseMin + pauseMax) / 2;

  let wallClockPerEmail = DEFAULT_PER_EMAIL_SEC / workers;
  let method = "estimated";

  if (done >= 10 && c.started_at) {
    const startedMs = new Date(c.started_at).getTime();
    const elapsed = Math.max(0, (now - startedMs) / 1000);
    const batchesCompleted = Math.max(0, Math.floor(done / batchSize));
    const elapsedPauses = batchesCompleted * avgPause;
    const pureSend = Math.max(0.5, elapsed - elapsedPauses);
    wallClockPerEmail = pureSend / done;
    method = "measured";
  }

  const remainingBatches = Math.ceil(remaining / batchSize);
  // Number of pauses left = remainingBatches - 1 if we're mid-batch,
  // remainingBatches otherwise. Approximate by remainingBatches - 1.
  const totalSend = remaining * wallClockPerEmail;
  const totalPause = Math.max(0, remainingBatches - 1) * avgPause;
  const seconds = Math.round(totalSend + totalPause);
  const percent = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

  return {
    seconds,
    human: humanDuration(seconds),
    method,
    perEmail: Number(wallClockPerEmail.toFixed(2)),
    remainingBatches,
    percent,
  };
}
